import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowRight, CheckCircle, Calculator, Info } from "lucide-react";

const PricingPage = () => {
  // Simplified pricing without size/sector selection

  const pricingData = {
    aspiring: {
      name: "Aspiring",
      badge: "Entry Level",
      badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/96hhy209_Badge%20Level%201.png",
      includes: [
        "Initial assessment across 6 pillars",
        "2-3 hours reviewer time",
        "Digital badge and certificate",
        "Directory listing",
        "Best practice submission access",
        "1-year certification validity"
      ]
    },
    inspired: {
      name: "Inspired", 
      badge: "Advanced",
      badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/r11ziiyu_Badge%20Level%202.png",
      includes: [
        "Comprehensive assessment with evidence review",
        "5-8 hours reviewer time",
        "Enhanced digital badge and assets",
        "Featured directory listing",
        "Best practice submission access",
        "1-year certification validity"
      ],
      popular: true
    },
    luminary: {
      name: "Luminary",
      badge: "Leadership", 
      badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/e1lw588c_Badge%20Level%203.png",
      includes: [
        "Expert-level assessment with site visits",
        "12-20 hours reviewer time",
        "Premium badge and marketing assets",
        "Priority directory placement",
        "Thought leadership opportunities", 
        "Mentor program eligibility",
        "1-year certification validity"
      ]
    }
  };

  // Removed pricing calculation functions

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="CNESS Certification Pricing - Starting from $600"
        description="CNESS certification pricing starts from $600 and varies based on organization size and revenue model. Choose your certification tier."
        url="/pricing"
      />
      
      <Header />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-8">
                Pricing starts from{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                  $600
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-12 leading-relaxed">
                Pricing varies based on organization's size and revenue model. Choose the certification tier that matches your organization's journey.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing section removed - pricing starts from $600 */}

        {/* Pricing Cards */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {Object.entries(pricingData).map(([key, tier]) => (
                  <Card 
                    key={key}
                    className={`relative hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ${
                      tier.popular ? 'ring-2 ring-amber-500 scale-105' : ''
                    }`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    
                    <CardContent className="p-8 text-center">
                      <div className="w-20 h-20 mx-auto mb-6">
                        <img src={tier.badgeImage} alt={`${tier.name} Badge`} className="w-full h-full object-contain" />
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                      <Badge variant="outline" className="mb-4">{tier.badge}</Badge>
                      
                      <div className="mb-6">
                        <div className="text-lg text-gray-600">
                          Contact for Pricing
                        </div>
                      </div>
                      
                      <div className="space-y-3 mb-8 text-left">
                        {tier.includes.map((item, index) => (
                          <div key={index} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                      
                      <Button 
                        className={`w-full ${
                          tier.popular 
                            ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                            : 'bg-gray-900 hover:bg-gray-800'
                        } text-white`}
                      >
                        Start Now
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What's Included Details */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                What's Included in Every Certification
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                {[
                  {
                    title: "Assessment Scope",
                    description: "Comprehensive evaluation across all 6 pillars of conscious enterprise"
                  },
                  {
                    title: "Reviewer Time", 
                    description: "Expert reviewer analysis and feedback on your submission"
                  },
                  {
                    title: "Badge Kit & Assets",
                    description: "Digital badges, certificates, and marketing materials"
                  },
                  {
                    title: "Directory Recognition",
                    description: "Listing in our verified conscious enterprise directory"
                  },
                  {
                    title: "Renewal Pathway",
                    description: "Clear process for maintaining and upgrading certification"
                  }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                      <Info className="h-6 w-6 text-amber-600" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;